import {createContext, useState, useContext, ReactNode, useEffect} from "react";
import {AdjustWindowResponseElement, ContentType, ModeStatus} from "../types/editorTypes";
import {tempKeyMoment, VolleyKeyMomentContent} from "../types/keyMomentTypes";
import {getEventById, getEventStreamFrameRate} from "../queries/eventQueries";
import {Competition, FootballMatch} from "../types/eventTypes";

// Tipo per i valori ammessi di "mode"


// Tipi per il contesto
interface SelectedContentContextType {
  selectedJobId: string | null;
  setSelectedJobId: (id: string | null) => void;
  selectedEventId: string | null;
  setSelectedEventId: (id: string | null) => void;
  selectedEventName: string | null;
  setSelectedEventName: (id: string | null) => void;
  selectedClip: any | null; // sostituisci "any" con il tipo corretto se disponibile
  setSelectedClip: (clip: any | null) => void;
  mode: ModeStatus;
  setMode: (mode: ModeStatus) => void;
  selectedKeyMoments: tempKeyMoment[]
  setSelectedKeyMoments: (selectedKeyMoments: tempKeyMoment[]) => void;
  maxEventDuration : number;
  setMaxEventDuration: (duration: number) => void;
  backupKeyMoments: tempKeyMoment[]
  setBackupKeyMoments: (backupKeyMoments: tempKeyMoment[]) => void;
  updateKeyMomentsWindow: (editElements: AdjustWindowResponseElement[]) => void
  updateKeyMoments: (keyMoment: tempKeyMoment[]) => void
  updateKeyMomentField: (keyMomentId: string, field: keyof tempKeyMoment, value: any ) => void
  streamUrl: string
  setStreamUrl: (url: string) => void
  contentType: ContentType | null
  setContentType: (contentType: ContentType | null) => void
  postCreationKeyMomentId: string | null,
  setPostCreationKeyMomentId: (id: string | null) => void,
  postCreationClipId: string | null,
  setPostCreationClipId: (id: string | null) => void,
  eventStreamFrameRate: number
  loadEventStreamFrameRate: (eventId: string) => Promise<number>
  matchEvent: FootballMatch | null,
  loadEvent: (eventId:string) => void,
  selectedCompetition: Competition | null,
  setSelectedCompetition: (competition: Competition | null) => void
}

// Creazione del contesto
const SelectedContentContext = createContext<SelectedContentContextType | undefined>(undefined);

// Hook personalizzato
export const useSelectedContent = (): SelectedContentContextType => {
  const context = useContext(SelectedContentContext);
  if (!context) {
    throw new Error("useSelectedContent must be used within a SelectedContentProvider");
  }
  return context;
};

// Tipi per le props del provider
interface SelectedContentProviderProps {
  children: ReactNode;
}

// Provider
export const SelectedContentProvider: React.FC<SelectedContentProviderProps> = ({ children }) => {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventStreamFrameRate, setEventStreamFrameRate] = useState<number>(25);
  const [matchEvent, setMatchEvent] = useState<FootballMatch | null>(null);
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const [selectedEventName, setSelectedEventName] = useState<string | null>(null);
  const [selectedClip, setSelectedClip] = useState<any | null>(null);
  const [mode, setMode] = useState<ModeStatus>("CREATE");
  const [contentType, setContentType] = useState<ContentType | null >(null);
  const [selectedKeyMoments, setSelectedKeyMoments] = useState<tempKeyMoment[]>([]);
  const [maxEventDuration, setMaxEventDuration] = useState(0);
  const [backupKeyMoments, setBackupKeyMoments] = useState<tempKeyMoment[]>([]);
  const [streamUrl, setStreamUrl] = useState<string>('');
  const [postCreationKeyMomentId, setPostCreationKeyMomentId] = useState<string | null>(null);
  const [postCreationClipId, setPostCreationClipId] = useState<string | null>(null);


    const updateKeyMomentsWindow = (editElements: AdjustWindowResponseElement[]) => {
        const tempKeyMoments = [...selectedKeyMoments]
        if(tempKeyMoments.length === 1 && tempKeyMoments[0].id === ""){
            tempKeyMoments[0].cut.coordinates = editElements[0].coordinates
            tempKeyMoments[0].cut.landmarks = editElements[0].landmarks
            tempKeyMoments[0].cut.trim_points = editElements[0].trim_points
        }else{
            for (const editsElement of editElements) {
                const keyMoment = tempKeyMoments.find(km => km.id === editsElement.discriminator)
                if (keyMoment) {
                    keyMoment.cut.coordinates = editsElement.coordinates
                    keyMoment.cut.landmarks = editsElement.landmarks
                    keyMoment.cut.trim_points = editsElement.trim_points
                }
                else{
                    console.error("No key moment found to update coordinates", editsElement.discriminator)
                }
            }
        }
        setSelectedKeyMoments(tempKeyMoments)
  }

  const updateKeyMoments = (keyMoments: tempKeyMoment[]) => {
    setSelectedKeyMoments(keyMoments)
  }

    const updateKeyMomentField = (keyMomentId: string, field: keyof tempKeyMoment | 'score', value: any) => {
        // Usiamo la funzione di callback della set per avere sempre lo stato più aggiornato
        setSelectedKeyMoments(prevKeyMoments =>
            prevKeyMoments.map(km => {
                // Se l'ID non corrisponde, restituiamo l'elemento così com'è
                if (km.id !== keyMomentId) {
                    return km;
                }

                // Se l'ID corrisponde, creiamo una COPIA dell'oggetto (...km) e la aggiorniamo
                if (field !== 'score') {
                    return {
                        ...km,
                        [field]: value
                    };
                } else {
                    // Tipizziamo la copia come VolleyKeyMomentContent
                    return {
                        ...km,
                        away_points: value.away_points,
                        home_points: value.home_points,
                        away_sets_won: value.away_sets_won,
                        home_sets_won: value.home_sets_won
                    } as any; // Usa il tipo corretto per il ritorno se necessario
                }
            })
        );
    };

  const loadEventStreamFrameRate = async (eventId: string) => {
      const frameRate = await getEventStreamFrameRate(eventId)
      setEventStreamFrameRate(frameRate)
      return frameRate
  }

    const loadEvent = async (eventId: string) => {
        const event = await getEventById(eventId, true)
        setMatchEvent(event)
        return matchEvent
    }

  return (
    <SelectedContentContext.Provider
      value={{
        selectedJobId,
        setSelectedJobId,
        selectedEventId,
        setSelectedEventId,
        selectedEventName,
        setSelectedEventName,
        selectedClip,
        setSelectedClip,
        mode,
        setMode,
        selectedKeyMoments,
        setSelectedKeyMoments,
        maxEventDuration, 
        setMaxEventDuration,
        backupKeyMoments,
        setBackupKeyMoments,
        updateKeyMomentsWindow,
        updateKeyMoments,
        updateKeyMomentField,
        setStreamUrl,
        streamUrl,
        contentType,
        setContentType,
        postCreationKeyMomentId,
        setPostCreationKeyMomentId,
        postCreationClipId,
        setPostCreationClipId,
        loadEventStreamFrameRate,
        eventStreamFrameRate,
        matchEvent,
        loadEvent,
        selectedCompetition,
        setSelectedCompetition
      }}
    >
      {children}
    </SelectedContentContext.Provider>
  );
};
